import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Avatar,
  Badge,
  Button,
  buttonClass,
  Callout,
  Card,
  DataTable,
  EmptyState,
  FormField,
  Modal,
  useToast,
} from "@promptgatewaysadmin/ui";
import { useSession } from "../auth/SessionProvider.js";
import {
  useInviteMember,
  useInvites,
  useMembers,
  useRemoveMember,
  useSubscriptionState,
  usePolicy,
} from "../queries/hooks.js";
import { seatState, type SubscriptionState } from "../api/normalise.js";
import { ErrorState, OfflineBanner, Skeleton, toPortalError } from "../components/states.js";
import { errorCopy } from "../copy/errors.js";
import { DateText } from "../components/format.js";
import { config } from "../config.js";

/**
 * /team
 *
 * Three role treatments, deliberately different:
 *
 *   sidebar nav      HIDDEN for members       — a nav item that always refuses is noise
 *   in-page controls VISIBLE, DISABLED, with a stated reason — a member should know the
 *                                               capability exists and who to ask
 *   email column     REMOVED, not blanked     — listMembers returns email: null for member
 *                                               callers by contract, and a column of empty
 *                                               cells reads as broken data
 */
export function Team() {
  const session = useSession();
  const sub = useSubscriptionState();
  const isAdmin = session.role === "admin";
  const members = useMembers();
  const invites = useInvites(isAdmin);
  const [inviting, setInviting] = useState(false);

  const state = sub.data;
  const locked = state?.status === "locked";
  const individual = state?.plan === "individual";

  if (individual) {
    // A one-seat org has nobody to share anything with. The admin links row still renders
    // ABOVE the upsell rather than being swallowed by it — API keys are available on
    // Individual, and a solo developer is a core API user.
    return (
      <div className="pg-page">
        {isAdmin && <AdminLinks />}
        <EmptyState
          title="Team features start on the Team plan"
          description="Invite colleagues, share one policy, and manage seats from one place. Team starts at 2 seats."
          actions={
            <Link className={buttonClass("primary")} to="/checkout?plan=team&interval=month&seats=2">
              See plans
            </Link>
          }
        />
      </div>
    );
  }

  return (
    <div className="pg-page">
      <OfflineBanner />

      {locked && (
        <Callout tone="warn" role="status" title="Your trial has ended">
          Your team is read-only until you choose a plan. Nothing was changed and nobody was
          removed.
        </Callout>
      )}

      {state && <SeatMeter state={state} isAdmin={isAdmin} locked={Boolean(locked)} onInvite={() => setInviting(true)} />}

      {members.isError ? (
        <ErrorState error={members.error} fallbackKey="error.team.load" onRetry={() => void members.refetch()} />
      ) : members.isPending ? (
        <Skeleton height={200} />
      ) : (
        <MemberTable rows={members.data.members} isAdmin={isAdmin} locked={Boolean(locked)} orgName={session.orgName ?? "your team"} />
      )}

      {isAdmin && invites.data && invites.data.length > 0 && (
        <Card title="Pending invites">
          <DataTable
            caption="Pending invites"
            rowKey={(r) => r.id}
            rows={invites.data}
            columns={[
              { key: "email", header: "Email", cell: (r) => r.email, rowHeader: true },
              { key: "role", header: "Role", cell: (r) => r.role },
              {
                key: "expires",
                header: "Expires",
                cell: (r) => (r.expiresAt ? <DateText iso={r.expiresAt} /> : "—"),
              },
            ]}
          />
        </Card>
      )}

      <PolicyCallout isAdmin={isAdmin} state={state} />

      {isAdmin && <AdminLinks />}

      {inviting && state && (
        <InviteModal state={state} onClose={() => setInviting(false)} />
      )}
    </div>
  );
}

/**
 * The seat meter has three states, and the third is an outage rather than a nudge.
 *
 * Seats are SPLIT-OWNED — Identity owns the consumed count, Billing owns the entitled
 * limit, reconciled asynchronously by event — so the two can disagree transiently, and the
 * over-limit state is reachable simply by Billing lowering a seat count.
 */
function SeatMeter({
  state,
  isAdmin,
  locked,
  onInvite,
}: {
  state: SubscriptionState;
  isAdmin: boolean;
  locked: boolean;
  onInvite: () => void;
}) {
  const seats = seatState(state);
  const tone = seats === "over" ? "danger" : seats === "at" ? "warn" : "info";
  const available = state.seatLimit - state.seatCount;

  const blockReason = !isAdmin
    ? "Only admins can invite members."
    : locked
      ? "Choose a plan to invite members."
      : state.status === "past_due"
        ? "Update your payment method first."
        : seats !== "under"
          ? `All ${state.seatLimit} seats are in use.`
          : null;

  return (
    <Card title="Seats">
      <Callout tone={tone} role="status">
        {seats === "over" ? (
          <>
            <strong>{state.seatCount - state.seatLimit} over your seat limit.</strong>{" "}
            Everyone&apos;s access is paused until you add seats or remove members.
          </>
        ) : seats === "at" ? (
          <>All {state.seatLimit} seats are in use.</>
        ) : (
          <>
            {state.seatCount} of {state.seatLimit} seats used · {available} available
          </>
        )}
      </Callout>

      <div className="pg-form__actions">
        {/* Visible-disabled with the reason in the accessible name's description — never a
            bare `disabled`, which would remove the button from the tab order and take the
            explanation with it. The server ALSO returns 409 at the limit; this block is a
            courtesy, never the enforcement. */}
        <Button
          aria-disabled={blockReason ? true : undefined}
          aria-describedby={blockReason ? "invite-reason" : undefined}
          onClick={() => !blockReason && onInvite()}
        >
          Invite member
        </Button>
        {blockReason && (
          <span id="invite-reason" className="pg-disabled-reason">
            {blockReason}
          </span>
        )}
        {isAdmin && seats !== "under" && (
          <Link className={buttonClass("secondary")} to="/subscription">
            Add seats ↗
          </Link>
        )}
      </div>
    </Card>
  );
}

function MemberTable({
  rows,
  isAdmin,
  locked,
  orgName,
}: {
  rows: readonly { userId: string; email?: string | null; displayName?: string | null; role: string; status: string; joinedAt?: string | null }[];
  isAdmin: boolean;
  locked: boolean;
  orgName: string;
}) {
  const remove = useRemoveMember();
  const toast = useToast();

  type Row = (typeof rows)[number];
  const columns = [
    {
      key: "name",
      header: "Name",
      rowHeader: true,
      cell: (r: Row) => (
        <span className="pg-member">
          <Avatar name={r.displayName || r.email || "?"} size="sm" />
          {r.displayName || r.email || "Invited"}
        </span>
      ),
    },
    // The email column is ABSENT for members, not blanked. listMembers returns email: null
    // for member callers by contract; a column of empty cells reads as broken data, while an
    // absent column reads as a different view.
    ...(isAdmin
      ? [{ key: "email", header: "Email", cell: (r: Row) => r.email ?? "—" }]
      : []),
    { key: "role", header: "Role", cell: (r: Row) => <Badge tone="neutral">{r.role}</Badge> },
    {
      key: "joined",
      header: "Joined",
      cell: (r: Row) => (r.joinedAt ? <DateText iso={r.joinedAt} /> : "—"),
    },
    ...(isAdmin
      ? [
          {
            key: "actions",
            header: "Actions",
            hideWhenStacked: false,
            align: "end" as const,
            cell: (r: Row) => (
              <Button
                size="sm"
                variant="ghost"
                aria-label={`Remove ${r.displayName || r.email || "member"}`}
                aria-disabled={locked || undefined}
                disabled={remove.isPending}
                onClick={async () => {
                  if (locked) return;
                  await remove.mutateAsync(r.userId);
                  toast.show(`${r.displayName || r.email} was removed.`);
                }}
              >
                Remove
              </Button>
            ),
          },
        ]
      : []),
  ];

  return (
    <DataTable
      caption={`Members of ${orgName}`}
      columns={columns}
      rows={rows}
      rowKey={(r) => r.userId}
      empty={<EmptyState title="Just you so far" description="Invite a colleague to share your plan and policy." />}
    />
  );
}

/**
 * The policy callout. Its copy adapts to the Guard entitlement, because redaction is
 * Guard-only and Guard is contact-sales — so the mock's copy over-claims for most orgs.
 */
function PolicyCallout({ isAdmin, state }: { isAdmin: boolean; state: SubscriptionState | undefined }) {
  const policy = usePolicy(Boolean(state));
  const guard = state?.products.includes("guard") ?? false;
  const doc = policy.data;

  const summary = doc
    ? `Redaction: ${doc.redaction.level} · ${doc.models.allowed.length} models allowed`
    : null;

  return (
    <Card title="Shared policy">
      <p className="pg-card__body">
        {guard
          ? "Redaction, allow-list, and disabled actions apply to everyone on your team, on every surface."
          : "Content moderation, model allow-list, and disabled actions apply to everyone on your team, on every surface."}
      </p>
      {summary && <p className="pg-card__body">{summary}</p>}
      {isAdmin ? (
        <Link className={buttonClass("secondary")} to="/team/policy">
          Edit policy
        </Link>
      ) : (
        <p className="pg-card__body">Set by your team admin.</p>
      )}
    </Card>
  );
}

/**
 * Admin sub-routes are reached from here, NOT the sidebar: six top-level nav items is
 * already the ceiling for a 230px rail, and they are occasional admin tasks. API keys is
 * not a live surface and is deliberately not linked.
 */
function AdminLinks() {
  return (
    <nav className="pg-adminlinks" aria-label="Team administration">
      <Link to="/team/policy">Shared policy</Link>
      <span className="pg-adminlinks__soon">SSO · SCIM — contact support</span>
    </nav>
  );
}

function InviteModal({ state, onClose }: { state: SubscriptionState; onClose: () => void }) {
  const invite = useInviteMember();
  const toast = useToast();
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<"admin" | "member">("member");

  const failure = toPortalError(invite.error);

  return (
    <Modal
      open
      onClose={onClose}
      title="Invite a member"
      description={`They'll get an email with a link to join ${state.plan === "enterprise" ? "Enterprise" : "Team"}.`}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            disabled={invite.isPending || !email}
            onClick={async () => {
              try {
                await invite.mutateAsync({ email, role });
                toast.show(`Invite sent to ${email}.`);
                onClose();
              } catch {
                /* rendered below; the modal stays open so the address is not lost */
              }
            }}
          >
            {invite.isPending ? "Sending…" : "Send invite"}
          </Button>
        </>
      }
    >
      <FormField
        label="Email"
        type="email"
        required
        value={email}
        onChange={(e) => setEmail(e.currentTarget.value)}
      />
      <FormField
        as="select"
        label="Role"
        value={role}
        onChange={(e) => setRole(e.currentTarget.value as "admin" | "member")}
      >
        <option value="member">Member</option>
        <option value="admin">Admin</option>
      </FormField>

      {failure && (
        <Callout tone="danger" role="alert">
          {errorCopy(failure.copyKey, { email, seats: state.seatLimit }).body}
          {failure.copyKey === "error.team.seatLimit" && (
            <>
              {" "}
              <a href={`${config.portalOrigin}/subscription`}>Add seats ↗</a>
            </>
          )}
        </Callout>
      )}
    </Modal>
  );
}
